import { DataLoadError, Loader } from '@creditsights/styleguide-web';
import { DndContext, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import classNames from 'classnames/bind';
import isEmpty from 'lodash/isEmpty';
import React, { ReactElement, ReactNode, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { ROUTES } from '../../../Base.constants';
import { DragOverlayProvider } from '../../../components/DragOverlay';
import { LockedContent } from '../../../components/LockedContent';
import SortableElementsList from '../../../components/SortableElementsList';
import CompanyOverviewWidget from '../../../components/widgets/CompanyOverview/CompanyOverviewWidget';
import FundamentalsWidget from '../../../components/widgets/Fundamentals';
import RiskOverviewWidget from '../../../components/widgets/RiskOverview';
import RiskProductsWidget from '../../../components/widgets/RiskProducts/RiskProductsWidget';
import CqsSummaryWidget from '../../../components/widgets/RiskSummary/CqsSummaryWidget';
import CreSummaryWidget from '../../../components/widgets/RiskSummary/CreSummaryWidget';
import { useDocumentTitle } from '../../../hooks/common';
import useGetRiskColumnView from '../../../hooks/company/useGetRiskColumnView';
import useCollisionDetection from '../../../hooks/dragAndDrop/useCollisionDetection';
import useOnDragRisk from '../../../hooks/dragAndDrop/useOnDragRisk';
import useFetchUserPermissions from '../../../hooks/permissions/useFetchUserPermissions';
import usePrepareRiskData from '../../../hooks/riskProducts/useGetRiskData';
import { FilterType } from '../../../models';
import { getCompanyId } from '../../../utils/tagUtils';
import { CompanyOutletContext } from '../Company';
import { CompanyWidgetId, Container, RISK_TAB_WIDGET_ID } from '../Company.constants';
import { getFilteredWidgets } from './CompanyRiskTab.logic';
import styles from './CompanyRiskTab.module.scss';

const cx = classNames.bind(styles);

const CompanyRiskTab = (): ReactElement => {
  const { company } = useOutletContext<CompanyOutletContext>();
  const issuerId = getCompanyId(company.tagId);
  const columnView = useGetRiskColumnView();
  const navigate = useNavigate();
  const { data: userPermissions } = useFetchUserPermissions();
  const { onDragStart, onDragEnd, onDragOver, draggableState } = useOnDragRisk(columnView);
  const { leftSideWidgets, middleWidgets, rightSideWidgets, activeContainer } = draggableState;
  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));
  const customCollisionDetection = useCollisionDetection();

  const { riskMetrics, riskOverviewMetrics, fundamentalsMetrics, isLoading, errorStates, refetch } =
    usePrepareRiskData(issuerId);
  const isError =
    errorStates.isRiskOverviewMetricsError && errorStates.isRiskMetricsError && errorStates.isFundamentalsError;

  const filterWidgets = (widgets: CompanyWidgetId[]) =>
    getFilteredWidgets(widgets, riskMetrics, riskOverviewMetrics, fundamentalsMetrics, errorStates);

  const documentTitle = company ? `${company.displayName}: Risk` : '';
  useDocumentTitle(documentTitle);

  useEffect(() => {
    if (!company.riskProductsCoverage) {
      navigate(ROUTES.notFound, { replace: true });
    }
  }, [userPermissions]);

  const renderWidget = (widgetId: CompanyWidgetId, isDragged?: boolean) => {
    switch (widgetId) {
      case RISK_TAB_WIDGET_ID.COMPANY_OVERVIEW:
        return (
          <CompanyOverviewWidget
            key={widgetId}
            company={company}
            showDragButton
            isDragged={isDragged}
            metadataOptions={[FilterType.region, FilterType.sector]}
            showDescription={false}
            showAsOfDate={false}
          />
        );
      case RISK_TAB_WIDGET_ID.RISK_PRODUCT:
        return (
          <RiskProductsWidget
            key={widgetId}
            companyTag={company.tagId}
            showDragButton
            isDragged={isDragged}
            dynamicHeader
          />
        );
      case RISK_TAB_WIDGET_ID.RISK_OVERVIEW:
        return (
          <RiskOverviewWidget
            key={widgetId}
            isError={errorStates.isRiskOverviewMetricsError}
            onErrorRetry={refetch}
            riskOverviewMetrics={riskOverviewMetrics!}
            showDragButton
            isDragged={isDragged}
          />
        );
      case RISK_TAB_WIDGET_ID.CQS_SUMMARY:
        return (
          <CqsSummaryWidget
            key={widgetId}
            isError={errorStates.isRiskOverviewMetricsError}
            onErrorRetry={refetch}
            riskOverviewMetrics={riskOverviewMetrics}
            showDragButton
            isDragged={isDragged}
          />
        );
      case RISK_TAB_WIDGET_ID.FUNDAMENTALS:
        return (
          <FundamentalsWidget
            fundamentalsMetrics={fundamentalsMetrics}
            key={widgetId}
            isError={errorStates.isFundamentalsError}
            onErrorRetry={refetch}
            showDragButton
            isDragged={isDragged}
          />
        );
      case RISK_TAB_WIDGET_ID.CRE_SUMMARY:
        return (
          <CreSummaryWidget
            key={widgetId}
            riskOverviewMetrics={riskOverviewMetrics}
            isError={errorStates.isRiskOverviewMetricsError}
            onErrorRetry={refetch}
            showDragButton
            isDragged={isDragged}
          />
        );
      default:
        return null;
    }
  };

  const getIsHighLighted = (containerId: string): boolean => {
    return (
      activeContainer === containerId ||
      (activeContainer === Container.LEFT && containerId === Container.RIGHT) ||
      (activeContainer === Container.RIGHT && containerId === Container.LEFT)
    );
  };

  const renderContainer = (
    container: Container,
    items: CompanyWidgetId[],
    classNames: string[] = []
  ): ReactNode | null => {
    if (isEmpty(items)) {
      return null;
    }

    return (
      <div
        className={cx(...classNames, {
          'side-container': container !== Container.MIDDLE,
          'middle-container': container === Container.MIDDLE,
          'is-highlighted': getIsHighLighted(container),
        })}
        data-testid={container}
      >
        <SortableElementsList renderElement={renderWidget} items={items} id={container} />
      </div>
    );
  };

  const renderSections = (
    leftSideWidgets: CompanyWidgetId[],
    middleWidgets: CompanyWidgetId[],
    rightSideWidgets: CompanyWidgetId[]
  ) => {
    switch (columnView) {
      case 1:
        return renderContainer(
          Container.MIDDLE,
          filterWidgets([...leftSideWidgets, ...middleWidgets, ...rightSideWidgets]),
          ['tablet']
        );
      case 2:
        return (
          <>
            {renderContainer(Container.LEFT, filterWidgets(leftSideWidgets))}
            {renderContainer(Container.MIDDLE, filterWidgets([...middleWidgets, ...rightSideWidgets]), [
              'tablet-landscape',
            ])}
          </>
        );
      case 3:
        return (
          <>
            {renderContainer(Container.LEFT, filterWidgets(leftSideWidgets))}
            {renderContainer(Container.MIDDLE, filterWidgets(middleWidgets), ['desktop'])}
            {renderContainer(Container.RIGHT, filterWidgets(rightSideWidgets))}
          </>
        );
      default:
        return undefined;
    }
  };
  const renderContent = () => {
    if (isLoading) {
      return (
        <div className={cx('state-wrapper')}>
          <Loader position="center" />
        </div>
      );
    }

    if (!userPermissions?.hasAccessToRP()) {
      return (
        <div className={cx('no-access')}>
          <LockedContent />
        </div>
      );
    }

    if (isError) {
      return (
        <div className={cx('state-wrapper')}>
          <DataLoadError onRetryClick={refetch} />
        </div>
      );
    }

    return (
      <DndContext
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragOver={onDragOver}
        collisionDetection={customCollisionDetection}
        sensors={sensors}
      >
        <DragOverlayProvider>{renderSections(leftSideWidgets, middleWidgets, rightSideWidgets)}</DragOverlayProvider>
      </DndContext>
    );
  };

  return (
    <div className={cx('body')}>
      <div className={cx('content')} data-testid="risk-tab-content">
        {renderContent()}
      </div>
    </div>
  );
};

export default CompanyRiskTab;
